<template>
  <div
    v-if="show"
    id="phrase-library-edit-modal"
    data-test="phrase-library-edit-modal"
    class="modal-mask"
    @click="handleBackdropClick"
  >
    <div class="phrase-edit-modal" @click.stop>
      <div class="phrase-edit-modal-content">
        <UserPhraseLibraryForm
          v-model:shorthand-value="editShorthand"
          v-model:expanded-value="editExpanded"
          :form-errors="formErrors"
          show-edit-elements
          @on-save="handleSave"
          @on-cancel="cancelAndClose"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, defineProps, defineEmits, watch, onUnmounted } from "vue";
import UserPhraseLibraryForm from "./UserPhraseLibraryForm.vue";
import { Phrase } from "../../../../Utils/phraselibrary";

const props = defineProps<{
  show: boolean;
  phrase: Phrase | null;
  formErrors: {
    shorthandErrors: string[] | null;
    expandedErrors: string[] | null;
  };
}>();
const emit = defineEmits(["close", "onSave"]);

const editShorthand = ref("");
const editExpanded = ref("");

watch(
  () => props.phrase,
  (newPhrase) => {
    if (newPhrase) {
      editShorthand.value = newPhrase.shorthand;
      editExpanded.value = newPhrase.expanded;
    } else {
      editShorthand.value = "";
      editExpanded.value = "";
    }
  },
  { immediate: true },
);

watch(
  () => props.show,
  (isVisible) => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  },
);

// Belt-and-braces cleanup: parents that mount this modal via `v-if` can tear
// it down before the watcher above sees `show` flip to false, leaving body
// { overflow: hidden } stuck.
onUnmounted(() => {
  if (document.body.style.overflow === "hidden") {
    document.body.style.overflow = "";
  }
});

const cancelAndClose = () => {
  emit("close");
};

const handleSave = (formData: {
  shorthandValue: string;
  expandedValue: string;
}) => {
  if (props.phrase) {
    const updatedPhrase = {
      ...props.phrase,
      shorthand: formData.shorthandValue,
      expanded: formData.expandedValue,
    };

    emit("onSave", updatedPhrase);
  }
};

const handleBackdropClick = (event: Event) => {
  if (event.target === event.currentTarget) {
    cancelAndClose();
  }
};
</script>
